import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { askParentCoach, getCoachHistory, CoachMessage } from '../services/parentApi';

const SUGGESTIONS = [
  'Comment va mon enfant dans ses révisions ?',
  'Quels sont ses progrès cette semaine ?',
  'Comment l\'aider à mieux s\'organiser ?',
  'Comment fonctionne Methodix ?',
  'Est-ce que le Premium vaut le coup ?',
];

export default function ChildCoach() {
  const { id } = useParams<{ id: string }>();
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) loadHistory();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadHistory() {
    try {
      const history = await getCoachHistory(id!);
      setMessages(history);
    } catch (err: any) {
      console.error('Error loading history:', err);
    } finally {
      setInitialLoading(false);
    }
  }

  async function handleSend(text?: string) {
    const message = text || input.trim();
    if (!message || !id || loading) return;

    setInput('');
    setError('');

    // Ajouter le message utilisateur immédiatement
    const userMsg: CoachMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const result = await askParentCoach(id, message);
      const assistantMsg: CoachMessage = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setRemaining(result.remaining_messages);
    } catch (err: any) {
      setError(err.message);
      // Retirer le message utilisateur si erreur
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Link to={`/child/${id}`} className="text-primary-600 hover:text-primary-700">
            ← Retour
          </Link>
          <h1 className="text-xl font-bold text-slate-900">🤖 Coach Methodix</h1>
        </div>
        {remaining !== null && (
          <span className="text-sm text-slate-500">
            {remaining} message{remaining !== 1 ? 's' : ''} restant{remaining !== 1 ? 's' : ''} aujourd'hui
          </span>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-4">
        {messages.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">🎓</div>
            <h2 className="text-lg font-semibold text-slate-700 mb-2">
              Bienvenue sur le Coach Parent
            </h2>
            <p className="text-slate-500 mb-4 max-w-md mx-auto">
              Posez vos questions sur les progrès de votre enfant, le fonctionnement de Methodix,
              ou demandez des conseils pour l'accompagner dans ses révisions.
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-800'
              }`}
            >
              {msg.role === 'assistant' && (
                <div className="text-xs text-slate-400 mb-1">🤖 Coach Methodix</div>
              )}
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
              <div className="flex space-x-1.5">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="mt-2 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
          {error}
        </div>
      )}

      {/* Suggestions */}
      {!loading && (
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((suggestion, i) => (
            <button
              key={i}
              onClick={() => handleSend(suggestion)}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-primary-50 hover:border-primary-300 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="mt-2 flex space-x-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Posez votre question au coach..."
          disabled={loading}
          className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
        />
        <button
          onClick={() => handleSend()}
          disabled={loading || !input.trim()}
          className="px-6 py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Envoyer
        </button>
      </div>
    </div>
  );
}
